import React, { useState, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Menu,
  ListItemIcon,

  Tooltip,
  InputAdornment,
  Chip,
  Stack,
  Divider,
  useTheme,
  useMediaQuery
} from "@mui/material";
import {

  FilterList as FilterIcon,

  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Description as DocIcon,
  Archive as ArchiveIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import adminFileService from "../services/adminFileService";

function FileManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileCourse, setFileCourse] = useState("manual");
  const [fileDescription, setFileDescription] = useState("");
  // UPDATED: Change from single targetRole to multiple targetRoles
  const [targetRoles, setTargetRoles] = useState(["installer"]);
  const [selectedRole, setSelectedRole] = useState("installer");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  // NEW: State for delete confirmation dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  // NEW: Pagination and Search states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalFiles, setTotalFiles] = useState(0);

  // NEW: State for actions menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFileForMenu, setSelectedFileForMenu] = useState(null);

  // NEW: State for selection
  const [selected, setSelected] = useState([]);

  // NEW: Handle Menu Open/Close
  const handleOpenMenu = (event, file) => {
    setAnchorEl(event.currentTarget);
    setSelectedFileForMenu(file);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedFileForMenu(null);
  };

  // NEW: Handle Selection
  const handleSelect = (fileName) => {
    setSelected((prev) =>
      prev.includes(fileName) ? prev.filter((n) => n !== fileName) : [...prev, fileName]
    );
  };

  const handleSelectAll = (checked) => {
    setSelected(checked ? files.map((f) => f.name) : []);
  };

  const roles = [
    { value: "installer", label: "Installer" },
    { value: "engineer", label: "Engineer" },
    { value: "architect", label: "Architect" },
    { value: "salesperson", label: "Salesperson" },
    // { value: "admin", label: "Admin" },
  ];

  const uploadRoles = [
    { value: "installer", label: "Installer" },
    { value: "engineer", label: "Engineer" },
    { value: "architect", label: "Architect" },
    { value: "salesperson", label: "Salesperson" },
  ];

  const loadFiles = useCallback(async () => {
    try {
      setLoading(true);

      // Pass pagination and search params
      const params = {
        page: page + 1, // API uses 1-based indexing
        limit: rowsPerPage,
        search: searchTerm || undefined,
      };

      const response = await adminFileService.getFilesByRole(selectedRole, params);
      setFiles(response.files || []);

      // Handle pagination info
      if (response.pagination) {
        setTotalFiles(response.pagination.total);
      } else {
        // Fallback
        setTotalFiles(response.total || response.files?.length || 0);
      }
    } catch (error) {
      console.error("Error loading files:", error);
      setSnackbar({
        open: true,
        message: "Failed to load files. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [selectedRole, page, rowsPerPage, searchTerm]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  // Reset pagination when filters change
  useEffect(() => {
    setPage(0);
  }, [selectedRole, searchTerm]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
    }
  }, []);

  const handleOpenUploadDialog = () => {
    setSelectedFile(null);
    setFileDescription("");
    setFileCourse("manual");

    // Auto-select current role when opening upload dialog
    if (selectedRole !== 'admin') {
      setTargetRoles([selectedRole]);
    } else {
      setTargetRoles(['installer']);
    }
    setOpenDialog(true);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
  });

  // UPDATED: Handle upload with multiple roles
  const handleUpload = async () => {
    if (!selectedFile || targetRoles.length === 0) return;

    setIsUploading(true);
    setOpenDialog(false);

    const fileId = Date.now();
    setUploadProgress({ [fileId]: 0 });

    try {
      const metadata = {
        course: fileCourse,
        description: fileDescription,
        onProgress: (progress) => {
          setUploadProgress((prev) => ({ ...prev, [fileId]: progress }));
        },
      };

      await adminFileService.uploadFileToMultipleRoles(
        selectedFile,
        targetRoles,
        metadata
      );

      // Clear progress and reload files
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[fileId];
        return newProgress;
      });

      setSnackbar({
        open: true,
        message: `File uploaded successfully to ${targetRoles.length
          } role(s): ${targetRoles.join(", ")}!`,
        severity: "success",
      });

      // Reload files if we're viewing any of the uploaded roles
      if (targetRoles.includes(selectedRole)) {
        loadFiles();
      }

      setSelectedFile(null);
      setFileDescription("");
      setFileCourse("manual");
      setTargetRoles(["installer"]); // Reset to default
    } catch (error) {
      console.error("Upload error:", error);
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[fileId];
        return newProgress;
      });
      setSnackbar({
        open: true,
        message: "Failed to upload file. Please try again.",
        severity: "error",
      });
    } finally {
      setIsUploading(false);
    }
  };

  // NEW: Open delete confirmation dialog
  const handleOpenDeleteDialog = (file) => {
    setFileToDelete(file);
    setOpenDeleteDialog(true);
  };

  // NEW: Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setFileToDelete(null);
  };

  // UPDATED: Confirm and execute delete
  const handleConfirmDeleteFile = async () => {
    if (!fileToDelete) return;

    try {
      await adminFileService.deleteFile(selectedRole, fileToDelete.name);
      setSnackbar({
        open: true,
        message: "File deleted successfully!",
        severity: "info",
      });
      handleCloseDeleteDialog();
      loadFiles(); // Reload files
    } catch (error) {
      console.error("Delete error:", error);
      setSnackbar({
        open: true,
        message: "Failed to delete file. Please try again.",
        severity: "error",
      });
    }
  };

  const handlePreviewFile = (file) => {
    try {
      // Use previewUrl directly from backend response
      window.open(file.previewUrl || file.url, "_blank", "noopener,noreferrer");

      setSnackbar({
        open: true,
        message: "Opening file preview...",
        severity: "info",
      });
    } catch (error) {
      console.error("Preview error:", error);
      setSnackbar({
        open: true,
        message: "Failed to preview file. Please try again.",
        severity: "error",
      });
    }
  };

  const handleDownloadFile = (file) => {
    try {
      // Use downloadUrl directly from backend response
      const link = document.createElement("a");
      link.href = file.downloadUrl || file.url;
      link.download = file.name;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSnackbar({
        open: true,
        message: "File download started...",
        severity: "success",
      });
    } catch (error) {
      console.error("Download error:", error);
      setSnackbar({
        open: true,
        message: "Failed to download file. Please try again.",
        severity: "error",
      });
    }
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    if (["pdf"].includes(extension)) return <PdfIcon />;
    if (["png", "jpg", "jpeg", "gif"].includes(extension)) return <ImageIcon />;
    if (["mp4", "avi", "mov"].includes(extension)) return <VideoIcon />;
    if (["doc", "docx"].includes(extension)) return <DocIcon />;
    if (["zip", "rar"].includes(extension)) return <ArchiveIcon />;
    return <FileIcon />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const clearFilters = () => {
    setSearchTerm("");
    setPage(0);
  };

  return (
    <Box sx={{ pb: { xs: 3, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          gap: { xs: 2, md: 0 },
          mb: { xs: 3, md: 4 },
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1, color: "primary.main" }}>
            File Management
          </Typography>
        </Box>
        {/* Actions Buttons - Desktop Only */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ width: { xs: "100%", md: "auto" }, display: { xs: "none", md: "flex" } }}
        >

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadFiles}
            disabled={loading}
            sx={{ borderRadius: 2, bgcolor: 'background.paper' }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenUploadDialog}
            sx={{ borderRadius: 2 }}
          >
            Upload File
          </Button>
        </Stack>
      </Box>

      {/* Mobile Sticky Bottom Action Bar */}
      <Paper
        elevation={8}
        sx={{
          display: { xs: "flex", md: "none" },
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          p: 1.5,
          gap: 1,
          justifyContent: "center",
          alignItems: "center",
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={loadFiles}
          disabled={loading}
          sx={{ borderRadius: 2, flex: 1, maxWidth: 150 }}
        >
          Refresh
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleOpenUploadDialog}
          sx={{ borderRadius: 2, flex: 1, maxWidth: 150 }}
        >
          Upload File
        </Button>
      </Paper>

      {/* Filters */}
      <Paper
        sx={{
          p: { xs: 2, sm: 3 },
          mb: { xs: 2, sm: 3 },
          borderRadius: 1,
          boxShadow: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FilterIcon color="primary" />
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold" }}
              color="text.primary"
            >
              Filters & Search
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            alignItems: { xs: "stretch", md: "center" },
            mb: 3
          }}
        >
          <Box sx={{ minWidth: { md: 250 }, flex: { md: "0 0 250px" } }}>
            <FormControl fullWidth>
              <InputLabel>Role Folder</InputLabel>
              <Select
                value={selectedRole}
                label="Role Folder"
                onChange={(e) => setSelectedRole(e.target.value)}
                sx={{
                  borderRadius: 1,
                  backgroundColor: "action.hover",
                  height: 48,
                  "&:hover": {
                    backgroundColor: "action.selected",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "background.paper",
                  },
                }}
              >
                {roles.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              flex: { xs: "1 1 100%", sm: "1 1 40%", md: "1 1 50%" },
              minWidth: { xs: "auto", sm: "280px" },
            }}
          >
            <TextField
              placeholder="Search by file name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="medium"
              fullWidth={isMobile}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                  backgroundColor: "action.hover",
                  height: 48,
                  "&:hover": {
                    backgroundColor: "action.selected",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "background.paper",
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Pagination Controls */}
          <Box
            sx={{
              flex: { xs: "1 1 100%", sm: "1 1 30%", md: "1 1 25%" },
              display: { xs: "none", md: "flex" },
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "flex-start" },
              gap: { xs: 1, sm: 1.5 },
              justifyContent: { xs: "stretch", sm: "flex-end" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                justifyContent: { xs: "space-between", md: "flex-end" },
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap", fontWeight: "medium", fontSize: "0.875rem" }}>
                Per page
              </Typography>
              <FormControl size="small" sx={{ minWidth: 70 }}>
                <Select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  displayEmpty
                  sx={{
                    borderRadius: 1,
                    backgroundColor: "background.paper",
                    height: 36,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "divider",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "primary.main",
                    },
                    "& .MuiSelect-select": {
                      py: 0.5,
                      px: 1,
                      fontSize: "0.875rem",
                      fontWeight: "medium",
                      color: "text.primary",
                    },
                  }}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              {/* Navigation Controls */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  justifyContent: { xs: "center", sm: "flex-end" },
                }}
              >
                <Tooltip title="First page">
                  <span>
                    <IconButton
                      onClick={() => setPage(0)}
                      disabled={page === 0}
                      size="small"
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        color: "text.primary",
                        "&:hover": {
                          backgroundColor: "primary.light",
                          color: "primary.main",
                        },
                        "&.Mui-disabled": {
                          color: "text.disabled",
                        },
                      }}
                    >
                      <FirstPageIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Previous page">
                  <span>
                    <IconButton
                      onClick={() => setPage(page - 1)}
                      disabled={page === 0}
                      size="small"
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        color: "text.primary",
                        "&:hover": {
                          backgroundColor: "primary.light",
                          color: "primary.main",
                        },
                        "&.Mui-disabled": {
                          color: "text.disabled",
                        },
                      }}
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Box
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    backgroundColor: "action.hover",
                    borderRadius: 1,
                    minWidth: { xs: 80, sm: 70 },
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{
                      fontWeight: "medium",
                      fontSize: "0.75rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {page + 1} / {Math.max(1, Math.ceil(totalFiles / rowsPerPage))}
                  </Typography>
                </Box>

                <Tooltip title="Next page">
                  <span>
                    <IconButton
                      onClick={() => setPage(page + 1)}
                      disabled={page >= Math.ceil(totalFiles / rowsPerPage) - 1}
                      size="small"
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        color: "text.primary",
                        "&:hover": {
                          backgroundColor: "primary.light",
                          color: "primary.main",
                        },
                        "&.Mui-disabled": {
                          color: "text.disabled",
                        },
                      }}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Last page">
                  <span>
                    <IconButton
                      onClick={() => setPage(Math.max(0, Math.ceil(totalFiles / rowsPerPage) - 1))}
                      disabled={page >= Math.ceil(totalFiles / rowsPerPage) - 1}
                      size="small"
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        color: "text.primary",
                        "&:hover": {
                          backgroundColor: "primary.light",
                          color: "primary.main",
                        },
                        "&.Mui-disabled": {
                          color: "text.disabled",
                        },
                      }}
                    >
                      <LastPageIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontWeight: "medium",
                  fontSize: "0.875rem",
                  whiteSpace: "nowrap",
                  mt: 2
                }}
              >
                Showing {Math.min(page * rowsPerPage + 1, totalFiles)} -{" "}
                {Math.min((page + 1) * rowsPerPage, totalFiles)} of {totalFiles}{" "}
                files
              </Typography>
            </Box>
          </Box>
        </Box>



        {/* Active Filters Chips */}
        {searchTerm && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: { xs: 0.5, sm: 1 },
              mt: 1,
              mb: 1
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mr: { xs: 0.5, sm: 1 },
                display: { xs: 'none', sm: 'block' }
              }}
            >
              Active Filters:
            </Typography>

            {searchTerm && (
              <Chip
                label={searchTerm}
                onDelete={() => setSearchTerm("")}
                color="primary"
                variant="outlined"
                size="small"
                sx={{
                  maxWidth: { xs: 120, sm: 200 },
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              />
            )}

            <Button
              size="small"
              onClick={clearFilters}
              startIcon={<ClearIcon />}
              variant="outlined"
              color="warning"
              sx={{
                borderRadius: 2,
                whiteSpace: "nowrap",
                px: { xs: 1, sm: 2 },
                minWidth: 'auto',
                "&:hover": {
                  backgroundColor: "warning.light",
                },
              }}
            >
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>CLEAR ALL</Box>
              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>Clear</Box>
            </Button>
          </Box>
        )}

        {/* Mobile Pagination - Compact Single Row */}
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            alignItems: "center",
            justifyContent: "space-between",
            pt: 1,
            mt: 1,
            borderTop: 1,
            borderColor: "divider"
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <FormControl size="small">
              <Select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                sx={{ height: 28, fontSize: '0.75rem', minWidth: 50 }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
            <IconButton
              onClick={() => setPage(0)}
              disabled={page === 0}
              size="small"
              sx={{ p: 0.5 }}
            >
              <FirstPageIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => setPage(page - 1)}
              disabled={page === 0}
              size="small"
              sx={{ p: 0.5 }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>

            <Typography variant="caption" sx={{ mx: 0.5, fontWeight: "medium", minWidth: 40, textAlign: 'center' }}>
              {page + 1}/{Math.max(1, Math.ceil(totalFiles / rowsPerPage))}
            </Typography>

            <IconButton
              onClick={() => setPage(page + 1)}
              disabled={page >= Math.ceil(totalFiles / rowsPerPage) - 1}
              size="small"
              sx={{ p: 0.5 }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => setPage(Math.max(0, Math.ceil(totalFiles / rowsPerPage) - 1))}
              disabled={page >= Math.ceil(totalFiles / rowsPerPage) - 1}
              size="small"
              sx={{ p: 0.5 }}
            >
              <LastPageIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50, textAlign: 'right' }}>
            {totalFiles} files
          </Typography>
        </Box>
      </Paper>

      {/* Upload Area - Dropzone is inside the dialog */}

      {/* Upload Progress */}
      {
        Object.keys(uploadProgress).length > 0 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Uploading...
            </Typography>
            {Object.entries(uploadProgress).map(([fileId, progress]) => (
              <Box key={fileId} sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {selectedFile?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {progress}%
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={progress} />
              </Box>
            ))}
          </Paper>
        )
      }

      {/* Files List */}
      <Box>
        {isMobile ? (
          <Stack spacing={1}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : files.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <FileIcon sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No files found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Upload files using the upload file button above
                </Typography>
              </Box>
            ) : (
              files.map((file) => (
                <Paper
                  key={file.key}
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    position: 'relative'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        color: "primary.main",
                        display: "flex",
                        p: 1,
                        bgcolor: 'action.hover',
                        borderRadius: 1
                      }}
                    >
                      {getFileIcon(file.name)}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="medium"
                        sx={{
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          lineHeight: 1.3
                        }}
                      >
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {formatFileSize(file.size)} • {new Date(file.lastModified).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={(e) => handleOpenMenu(e, file)}
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                </Paper>
              ))
            )}
          </Stack>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ borderRadius: 1, boxShadow: 2 }}
          >
            <Table sx={{ minWidth: 650 }} aria-label="file table">
              <TableHead>
                <TableRow sx={{ backgroundColor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem", width: "40%" }}>File Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem", width: "20%" }}>Size</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem", width: "25%" }}>
                    Last Modified
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold", fontSize: "0.875rem", width: "15%" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: "center", py: 4 }}>
                      <CircularProgress />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Loading files...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : files.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: "center", py: 4 }}>
                      <FileIcon sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No files found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Upload files using the upload file button above
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  files.map((file) => (
                    <TableRow
                      key={file.key}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                      hover
                    >
                      <TableCell component="th" scope="row">
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Box
                            sx={{
                              color: "primary.main",
                              mr: 2,
                              display: "flex",
                            }}
                          >
                            {getFileIcon(file.name)}
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "medium" }}
                          >
                            {file.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{formatFileSize(file.size)}</TableCell>
                      <TableCell>
                        {new Date(file.lastModified).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Actions">
                          <IconButton
                            onClick={(e) => handleOpenMenu(e, file)}
                            size="small"
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => {
          if (selectedFileForMenu) handlePreviewFile(selectedFileForMenu);
          handleCloseMenu();
        }}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>Preview</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedFileForMenu) handleDownloadFile(selectedFileForMenu);
          handleCloseMenu();
        }}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" color="success" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedFileForMenu) handleOpenDeleteDialog(selectedFileForMenu);
            handleCloseMenu();
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Upload Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload New File</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Dropzone Area */}
            <Paper
              {...getRootProps()}
              sx={{
                p: 3,
                mb: 3,
                border: "2px dashed",
                borderColor: isDragActive ? "primary.main" : "grey.300",
                backgroundColor: isDragActive ? "primary.light" : "grey.50",
                cursor: "pointer",
                textAlign: "center",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "primary.light",
                },
              }}
            >
              <input {...getInputProps()} />
              <CloudUploadIcon
                sx={{ fontSize: 40, color: "primary.main", mb: 1 }}
              />
              <Typography variant="body1" color="text.secondary">
                {isDragActive
                  ? "Drop the file here"
                  : selectedFile
                    ? "Click or drag to replace file"
                    : "Drag & drop file here, or click to select"}
              </Typography>
            </Paper>

            {selectedFile && (
              <Box sx={{ mb: 3, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Selected File:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                  {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Size: {formatFileSize(selectedFile.size)}
                </Typography>
              </Box>
            )}

            <FormControl fullWidth margin="normal">
              <InputLabel>Target Role Folder</InputLabel>
              <Select
                multiple
                value={targetRoles}
                label="Target Role Folder"
                onChange={(e) => setTargetRoles(e.target.value)}
                input={<OutlinedInput label="Target Role Folder" />}
                renderValue={(selected) =>
                  selected
                    .map(
                      (value) =>
                        uploadRoles.find((role) => role.value === value)?.label
                    )
                    .join(", ")
                }
              >
                {uploadRoles
                  .map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      <Checkbox
                        checked={targetRoles.indexOf(role.value) > -1}
                      />
                      <ListItemText primary={role.label} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {/* <TextField
              label="Description"
              value={fileDescription}
              onChange={(e) => setFileDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              margin="normal"
              placeholder="Enter file description..."
            /> */}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={isUploading}
          >
            Upload to {targetRoles.length} Role(s) Folder
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-confirmation-dialog-title"
        aria-describedby="delete-confirmation-dialog-description"
      >
        <DialogTitle id="delete-confirmation-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography id="delete-confirmation-dialog-description">
            Are you sure you want to delete the file "
            <strong>{fileToDelete?.name}</strong>" from the {selectedRole}{" "}
            folder?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteFile}
            color="secondary"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box >
  );
}

export default FileManagement;
